import { useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";

// A board's tab lives in ?view=, so a link, a refresh and Back all land on the
// same tab. The bare board path (/inquiries, /requisitions) opens the tab used
// last and rewrites the URL to name it. A dialog path (/inquiries/123) keeps
// its URL and shows the last tab behind the dialog.
function useListView<V extends string>(
  basePath: string,
  views: readonly V[],
  fallback: V,
  storageKey: string
): [V, (view: V) => void] {
  const history = useHistory();
  const location = useLocation();
  const [stored, setStored] = useStateWithLocalStorage<V>(fallback, storageKey);

  const param = new URLSearchParams(location.search).get("view");
  const fromUrl = views.find((v) => v === param);
  // A stored value from an older build may name a tab that no longer exists.
  const last = views.includes(stored) ? stored : fallback;
  const view = fromUrl ?? last;
  const onBoard = location.pathname.replace(/\/$/, "") === basePath;

  useEffect(() => {
    if (fromUrl) {
      if (fromUrl !== stored) setStored(fromUrl);
      return;
    }
    // Other query params (e.g. a legacy ?requisitionId=) are left to their
    // own redirect.
    if (onBoard && !location.search) {
      history.replace(`${basePath}?view=${view}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl, onBoard, location.search]);

  const setView = (next: V) => {
    setStored(next);
    history.push(`${basePath}?view=${next}`);
  };

  return [view, setView];
}

export default useListView;
